import { useState, useCallback, Fragment } from 'react';
import { useMutation } from '@apollo/client/react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { makeStyles } from 'tss-react/mui';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import MLink from '@mui/material/Link';
import { updateEventGroupApprovalMutation } from '../../apollo/events';
import RadioGroupField from '../forms/radio-group-field';
import TextAreaField from '../forms/text-area-field';
import Host from '../host';
import MaterialLink from '../material-link';
import { momentParse } from '../../utils/time';
import { eventPath } from '../../utils/slugify';
import type { Event } from '../../types/Event';

const useStyles = makeStyles()((theme) => ({
  eventName: {
    fontWeight: 'bold',
  },
  textArea: {
    minWidth: 400,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
    },
  },
}));

const formSchema = Yup.object().shape({
  approved: Yup.bool().required('Approved is required'),
  groupDeniedMessage: Yup.string().nullable(),
});

type Props = {
  event: Event;
  buttonComponent?: React.ElementType;
  onSuccess?: (...args: unknown[]) => void;
  [key: string]: unknown;
};

const GroupPendingButton = (props: Props) => {
  const { event, buttonComponent: ButtonComponent = Button, onSuccess, ...rest } = props;
  const [open, setOpen] = useState(false);
  const { classes } = useStyles();

  const handleClickOpen = useCallback((e) => {
    e.preventDefault();
    setOpen(true);
  }, []);

  const handleCancel = useCallback((e) => {
    e.preventDefault();
    setOpen(false);
  }, []);

  const [updateEventGroupApproval] = useMutation(updateEventGroupApprovalMutation);

  const handleSubmit = useCallback((values) => {
    updateEventGroupApproval({
      variables: {
        id: event.id,
        data: values,
      },
    }).then(() => {
      setOpen(false);
      if (onSuccess) {
        onSuccess();
      }
    });
  }, [event, onSuccess, updateEventGroupApproval]);

  return (
    <Fragment>
      <ButtonComponent onClick={handleClickOpen} {...rest}>
        Respond to Approval Request
      </ButtonComponent>
      <Dialog
        open={open}
        onClose={handleCancel}
        aria-labelledby="pending-approval-title"
        aria-describedby="pending-approval-description"
      >
        <Formik
          initialValues={{
            approved: true,
            groupDeniedMessage: '',
          }}
          validationSchema={formSchema}
          onSubmit={handleSubmit}
        >
          {({ values }) => (
            <Form>
              <DialogTitle id="pending-approval-title">Approve this event?</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  <MaterialLink
                    href={eventPath(event.id, event.name)}
                    className={classes.eventName}
                  >
                    {event.name} ({momentParse(event.startAt, event.timezone).format('MMMM D, YYYY h:mm a')})
                  </MaterialLink>
                  <Host event={event} />
                </DialogContentText>
                <RadioGroupField
                  name="approved"
                  options={[
                    { value: true, label: 'Approve' },
                    { value: false, label: 'Deny' },
                  ]}
                />
                {!values.approved && (
                  <TextAreaField
                    name="groupDeniedMessage"
                    label="Denied Message (optional)"
                    className={classes.textArea}
                    helperText={(
                      <span>
                        <MLink href="https://www.markdownguide.org/basic-syntax/" target="_blank">
                          Markdown
                        </MLink>
                        {' '}is supported
                      </span>
                    )}
                  />
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCancel}>
                  Cancel
                </Button>
                <Button type="submit" color="primary" autoFocus>
                  Confirm
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Fragment>
  );
};

export default GroupPendingButton;
